import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Building2, Users, KeyRound, PauseOctagon, PlayCircle, Plus, Trash2, RefreshCw, Crown, UserPlus, Copy, ExternalLink, UserCog } from "lucide-react";

/**
 * Platform Owner console — only visible to role=platform_owner.
 * Lives at /platform/tenants. Lists every ARN-holder firm using the
 * platform, lets you create a new tenant, kill-switch any one of them,
 * and impersonate them for support.
 */
export default function PlatformTenants() {
  const { user, api, impersonateTenant } = useAuth();
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    firm_name: "", arn_code: "", owner_name: "", owner_email: "", owner_phone: "", plan: "starter", notes: "",
  });
  const [filterStatus, setFilterStatus] = useState("");
  const [ownerInfo, setOwnerInfo] = useState(null); // { tenant, setup_link, temporary_password }

  const STATUS_STYLE = {
    active:    "bg-emerald-100 text-emerald-800 border-emerald-300",
    trial:     "bg-sky-100 text-sky-800 border-sky-300",
    past_due:  "bg-amber-100 text-amber-800 border-amber-300",
    suspended: "bg-red-100 text-red-800 border-red-300",
    cancelled: "bg-gray-200 text-gray-700 border-gray-300",
  };
  const PLAN_STYLE = {
    starter:    "bg-sky-50 text-sky-700 border-sky-200",
    pro:        "bg-[#E5D4A8]/40 text-[#07111F] border-[#C8A96B]",
    enterprise: "bg-[#1a3f3a] text-white border-[#1a3f3a]",
    internal:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${encodeURIComponent(filterStatus)}` : "";
      const [r, m] = await Promise.all([
        api.get(`/platform/tenants${params}`),
        api.get("/platform/tenants/_meta/plans"),
      ]);
      setRows(r.data || []);
      setPlans((m.data?.plans) || []);
      setStatuses((m.data?.statuses) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, [api, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const createTenant = useCallback(async () => {
    if (!form.firm_name || !form.owner_email || !form.owner_name) {
      toast.error("Firm name, owner name and owner email are required");
      return;
    }
    try {
      await api.post("/platform/tenants", form);
      toast.success(`Tenant created — ${form.firm_name} is in trial`);
      setCreateOpen(false);
      setForm({ firm_name: "", arn_code: "", owner_name: "", owner_email: "", owner_phone: "", plan: "starter", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  }, [form, api, load]);

  const suspend = async (t) => {
    const reason = window.prompt(`Reason to suspend "${t.firm_name}"? (this locks all their users out)`);
    if (reason === null) return;
    try {
      await api.post(`/platform/tenants/${t.id}/suspend`, null, { params: { reason } });
      toast.success(`Suspended ${t.firm_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suspend failed"); }
  };
  const activate = async (t) => {
    try {
      await api.post(`/platform/tenants/${t.id}/activate`);
      toast.success(`Activated ${t.firm_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Activate failed"); }
  };
  const cancel = async (t, hard = false) => {
    const word = hard ? "HARD-DELETE" : "cancel";
    const ans = window.prompt(`Type "${word}" to confirm ${word} of "${t.firm_name}". ${hard ? "This irreversibly wipes ALL their data." : "Soft-cancel keeps data 90 days."}`);
    if (ans !== word) return;
    try {
      await api.delete(`/platform/tenants/${t.id}`, { params: hard ? { hard: true } : {} });
      toast.success(hard ? `Hard-deleted ${t.firm_name}` : `Soft-cancelled ${t.firm_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const createOwner = async (t) => {
    if (!window.confirm(`Create the owner login for "${t.firm_name}"?\n\nThis provisions ${t.owner_email} as their super_admin and generates a 72-hour setup link.`)) return;
    try {
      const r = await api.post(`/platform/tenants/${t.id}/create-owner`);
      setOwnerInfo({ tenant: t, ...r.data });
      toast.success(`Owner login provisioned for ${t.firm_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create owner login"); }
  };

  const impersonate = async (t) => {
    if (!window.confirm(`Step into ${t.firm_name} as their owner?\n\nYou'll see only their data, scoped to tenant_id="${t.id}". A red banner stays on screen so you never forget you're impersonating. Click "Exit impersonation" any time to return here.`)) return;
    try {
      await impersonateTenant(t.id);
      toast.success(`Now impersonating ${t.firm_name}`);
      // Hard navigate so the entire app re-bootstraps under the
      // impersonation token and re-fetches /auth/me.
      window.location.replace("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed — has an owner login been created?");
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null); // {done, total, percent, current_collection, rows_stamped}
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthReport, setHealthReport] = useState(null); // {collections_with_rows, suspicious_collections, report}

  const runDataHealth = async (deep = false) => {
    setHealthLoading(true);
    try {
      // Fast mode by default — pure count_documents queries, ~2-5s
      // even on 1.3M rows. Deep mode adds histogram aggregations and
      // is only triggered by the "Deep inspect" button after the fast
      // check has flagged suspicious collections.
      const r = await api.get(`/platform/tenants/_admin/tenant-data-health${deep ? "?deep=true" : ""}`, {
        timeout: 120000,  // 2 minutes — deep mode on production can take a minute
      });
      setHealthReport(r.data);
      const suspect = (r.data?.suspicious_collections || []).length;
      if (suspect === 0) {
        toast.success("All collections clean — every row is tagged tenant_id='kinntegra'.");
      } else {
        toast.warning(`${suspect} suspicious collection(s) found — see panel below.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.code || e?.message || "Unknown error";
      toast.error(`Data health check failed: ${detail}`);
    } finally {
      setHealthLoading(false);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm("Stamp tenant_id='kinntegra' on every legacy row across every collection?\n\nThis is idempotent — safe to run multiple times. The job runs in the background and can take 1–3 minutes for ~1.3 million rows. You'll see live progress below.")) return;
    setBackfilling(true);
    setBackfillProgress({ done: 0, total: 0, percent: 0, current_collection: "starting…", rows_stamped: 0 });
    try {
      const r = await api.post("/platform/tenants/_admin/backfill-kinntegra");
      if (r.data?.running === true && r.data?.ok === false) {
        toast.info("A backfill is already in progress — picking up live progress.");
      }
      // Poll every 2 seconds until finished (finished_at !== null) or max 5 minutes
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const s = await api.get("/platform/tenants/_admin/backfill-kinntegra/status");
          const st = s.data || {};
          setBackfillProgress({
            done: st.collections_done || 0,
            total: st.collections_total || 0,
            percent: st.percent || 0,
            current_collection: st.current_collection || (st.finished_at ? "complete" : "starting…"),
            rows_stamped: st.rows_stamped || 0,
          });
          if (st.finished_at) {
            if (st.error) {
              toast.error("Backfill failed: " + st.error);
            } else if (st.rows_stamped > 0) {
              toast.success(`Stamped ${st.rows_stamped.toLocaleString()} rows across ${st.collections_done} collections`);
            } else {
              toast.success("Already fully stamped — nothing to do.");
            }
            setBackfilling(false);
            return;
          }
          if (Date.now() - startedAt > 5 * 60 * 1000) {
            toast.error("Backfill is taking longer than 5 minutes — check the Tenants Console later for the final count.");
            setBackfilling(false);
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          // Transient poll error — retry; the worker keeps running
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 1000);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Backfill failed to start: ${detail}`);
      setBackfilling(false);
    }
  };

  if (user?.role !== "platform_owner") {
    return (
      <Card className="max-w-xl mx-auto mt-12">
        <CardContent className="p-8 text-center">
          <Crown className="mx-auto mb-3 text-rose-500" size={36} />
          <h2 className="text-xl font-semibold mb-1">Platform Owner only</h2>
          <p className="text-sm text-gray-500">This console is restricted to the platform founder.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-tenants-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 size={26} className="text-[#1a3f3a]" />
            Tenants Console
            <Badge variant="outline" className="ml-2 bg-[#f7f5f0] text-[#07111F] border-[#C8A96B]/50">PLATFORM OWNER</Badge>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Every ARN-holder firm onboarded to Kinntegra. Their data is fully isolated from yours.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="tenant-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" size="sm" data-testid="tenant-refresh"><RefreshCw size={16} /></Button>
          <Button
            onClick={runDataHealth}
            variant="outline"
            size="sm"
            disabled={healthLoading}
            data-testid="tenant-data-health-button"
            title="Show a per-collection histogram of tenant_id values. Use this when backfill says '0 rows stamped' but data is still missing — it'll reveal any case-variant or alternate-ID rows that need normalizing."
            className="text-[#1a3f3a] border-[#C8A96B]/40 hover:bg-[#C8A96B]/10"
          >
            {healthLoading ? "Checking…" : "Data health"}
          </Button>
          <Button
            onClick={runBackfill}
            variant="outline"
            size="sm"
            disabled={backfilling}
            data-testid="tenant-backfill-button"
            title="Stamp tenant_id='kinntegra' on every legacy row across every collection. Safe to run multiple times — only touches rows missing the field. Run this once on production after redeploy if sub-brokers report empty dashboards."
            className="text-[#1a3f3a] border-[#C8A96B]/40 hover:bg-[#C8A96B]/10"
          >
            {backfilling ? "Backfilling…" : "Run Kinntegra backfill"}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#1a3f3a] hover:bg-[#07111F]" data-testid="tenant-new">
            <Plus size={16} className="mr-1" />Onboard ARN holder
          </Button>
        </div>
      </div>

      {healthReport && (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="data-health-panel">
          <div className="flex justify-between items-center mb-3">
            <div className="font-medium text-[#1a3f3a]">
              Tenant Data Health · {healthReport.collections_with_rows} collections checked
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(healthReport, null, 2));
                toast.success("Copied full report to clipboard");
              }}
              className="text-xs text-gray-500 hover:text-[#1a3f3a] underline"
              data-testid="copy-health-report"
            >
              Copy JSON
            </button>
          </div>
          {(healthReport.suspicious_collections || []).length === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              ✓ All rows in every collection are correctly tagged <code>tenant_id="kinntegra"</code>.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex justify-between items-center">
                <span>⚠ {healthReport.suspicious_collections.length} collection(s) contain rows with a tenant_id other than <code>"kinntegra"</code>. Run the backfill below — the new matcher will normalize them.</span>
                {!healthReport.deep && (
                  <button
                    onClick={() => runDataHealth(true)}
                    disabled={healthLoading}
                    className="ml-3 text-xs underline hover:text-amber-900 disabled:opacity-50"
                    data-testid="deep-inspect-button"
                  >
                    {healthLoading ? "Inspecting…" : "Deep inspect (histogram)"}
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium">Collection</th>
                      <th className="px-3 py-2 font-medium text-right">Total rows</th>
                      <th className="px-3 py-2 font-medium text-right">Non-kinntegra</th>
                      <th className="px-3 py-2 font-medium">Tenant_id values (histogram)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthReport.suspicious_collections.map((s, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 font-mono">{s.collection}</td>
                        <td className="px-3 py-2 text-right">{(s.total || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-amber-700 font-medium">{(s.non_kinntegra_rows || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 font-mono text-[10px]">
                          {Object.entries(s.histogram || {}).map(([k, v]) => (
                            <span key={k} className={`inline-block mr-2 ${k === "kinntegra" ? "text-emerald-700" : "text-amber-700"}`}>
                              {k}: {v.toLocaleString()}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {backfillProgress && (
        <div className="bg-[#fff8e8] border border-[#C8A96B]/40 rounded-lg p-4" data-testid="backfill-progress-panel">
          <div className="flex justify-between items-center text-sm mb-2">
            <div className="font-medium text-[#1a3f3a]">
              {backfilling ? "Backfilling tenant_id…" : "Backfill complete"}
            </div>
            <div className="text-xs text-gray-600">
              {backfillProgress.done}/{backfillProgress.total} collections · {backfillProgress.rows_stamped.toLocaleString()} rows stamped
            </div>
          </div>
          <div className="w-full bg-white rounded-full h-2 overflow-hidden border border-[#C8A96B]/30">
            <div
              className="bg-[#C8A96B] h-2 transition-all duration-300 ease-out"
              style={{ width: `${backfillProgress.percent}%` }}
            />
          </div>
          {backfillProgress.current_collection && backfilling && (
            <div className="text-[11px] text-gray-500 mt-1.5">
              Currently stamping: <span className="font-mono">{backfillProgress.current_collection}</span> · {backfillProgress.percent}%
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { k: "active",   label: "Active",    color: "emerald" },
          { k: "trial",    label: "Trial",     color: "sky" },
          { k: "past_due", label: "Past due",  color: "amber" },
          { k: "suspended",label: "Suspended", color: "rose" },
          { k: "cancelled",label: "Cancelled", color: "gray" },
        ].map(s => {
          const n = rows.filter(r => r.status === s.k).length;
          return (
            <Card key={s.k} className={`bg-${s.color}-50 border-${s.color}-200`}>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</div>
                <div className={`text-2xl font-bold text-${s.color}-700 mt-1`}>{n}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Tenants ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-400">Loading tenants…</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">No tenants yet. Click &quot;Onboard ARN holder&quot; to add the first one.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-2.5">Firm</th>
                    <th className="px-4 py-2.5">Owner</th>
                    <th className="px-4 py-2.5">ARN</th>
                    <th className="px-4 py-2.5">Plan</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Users</th>
                    <th className="px-4 py-2.5 text-right">Sub-brokers</th>
                    <th className="px-4 py-2.5 text-right">Clients</th>
                    <th className="px-4 py-2.5">Last login</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} className={i % 2 ? "bg-white" : "bg-gray-50/30"} data-testid={`tenant-row-${i}`}>
                      <td className="px-4 py-2.5 font-medium">
                        {r.firm_name}
                        {r.id === "kinntegra" && <Crown size={12} className="inline ml-1 text-amber-500" title="Internal Kinntegra tenant" />}
                      </td>
                      <td className="px-4 py-2.5">
                        <div>{r.owner_name}</div>
                        <div className="text-xs text-gray-500">{r.owner_email}</div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{r.arn_code || "—"}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={PLAN_STYLE[r.plan] || ""}>{r.plan}</Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={STATUS_STYLE[r.status] || ""}>{r.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">{r.user_count || 0}</td>
                      <td className="px-4 py-2.5 text-right" data-testid={`tenant-sub-broker-count-${i}`}>{r.sub_broker_count || 0}</td>
                      <td className="px-4 py-2.5 text-right">{r.client_count || 0}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{r.last_login_at ? new Date(r.last_login_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {r.id !== "kinntegra" && (
                            <Button size="sm" variant="outline" onClick={() => createOwner(r)} className="h-7 text-xs text-emerald-700 border-emerald-200" data-testid={`tenant-create-owner-${i}`} title="Provision owner login + generate setup link">
                              <UserPlus size={13} />
                            </Button>
                          )}
                          {r.id !== "kinntegra" && r.status !== "suspended" && r.status !== "cancelled" && (
                            <Button size="sm" variant="outline" onClick={() => impersonate(r)} className="h-7 text-xs text-[#1a3f3a] border-[#C8A96B]/60 bg-[#fff8e8]" data-testid={`tenant-impersonate-${i}`} title="Impersonate this tenant — see their data without a password">
                              <UserCog size={13} />
                            </Button>
                          )}
                          {r.id !== "kinntegra" && r.status !== "suspended" && (
                            <Button size="sm" variant="outline" onClick={() => suspend(r)} className="h-7 text-xs" data-testid={`tenant-suspend-${i}`} title="Kill switch — instant lockout">
                              <PauseOctagon size={13} />
                            </Button>
                          )}
                          {r.id !== "kinntegra" && (r.status === "suspended" || r.status === "trial" || r.status === "past_due") && (
                            <Button size="sm" variant="outline" onClick={() => activate(r)} className="h-7 text-xs text-emerald-700 border-emerald-200" data-testid={`tenant-activate-${i}`} title="Activate">
                              <PlayCircle size={13} />
                            </Button>
                          )}
                          {r.id !== "kinntegra" && (
                            <Button size="sm" variant="outline" onClick={() => cancel(r, false)} className="h-7 text-xs text-[#07111F] border-[#C8A96B]/50" data-testid={`tenant-cancel-${i}`} title="Soft-cancel (90-day retention)">
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Onboard new ARN holder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Firm name *</label>
              <Input value={form.firm_name} onChange={e => setForm({...form, firm_name: e.target.value})} placeholder="Prosperity Wealth Pvt Ltd" data-testid="tenant-form-firm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Owner name *</label>
                <Input value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} data-testid="tenant-form-owner-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">ARN code</label>
                <Input value={form.arn_code} onChange={e => setForm({...form, arn_code: e.target.value})} placeholder="ARN-160568" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Owner email *</label>
                <Input type="email" value={form.owner_email} onChange={e => setForm({...form, owner_email: e.target.value})} placeholder="owner@prosperity.in" data-testid="tenant-form-owner-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Owner phone</label>
                <Input value={form.owner_phone} onChange={e => setForm({...form, owner_phone: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Plan</label>
              <Select value={form.plan} onValueChange={v => setForm({...form, plan: v})}>
                <SelectTrigger data-testid="tenant-form-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.filter(p => p.id !== "internal").map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — ₹{p.price_mo.toLocaleString()}/mo</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Notes</label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional — internal comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#1a3f3a] hover:bg-[#07111F]" onClick={createTenant} data-testid="tenant-form-submit">
              <KeyRound size={14} className="mr-1.5" />Create tenant (trial)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner-login setup modal — shown right after createOwner() */}
      <Dialog open={!!ownerInfo} onOpenChange={(o) => !o && setOwnerInfo(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={18} className="text-emerald-600" /> Owner login provisioned
            </DialogTitle>
          </DialogHeader>
          {ownerInfo && (
            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{ownerInfo.tenant?.firm_name}</span> · {ownerInfo.email}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <p className="font-medium mb-1">Share this setup link with the owner</p>
                <p>The link is valid for <b>{ownerInfo.setup_expires_in_hours} hours</b>. They click → set their own password &amp; PIN → ready to use the platform. <b>You never need to know their password.</b></p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Setup link</label>
                <div className="flex gap-2 mt-1">
                  <input
                    readOnly
                    value={ownerInfo.setup_link}
                    className="flex-1 border rounded-md px-3 py-2 text-xs font-mono bg-gray-50 truncate"
                    data-testid="owner-setup-link"
                  />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(ownerInfo.setup_link); toast.success("Setup link copied"); }} title="Copy" data-testid="owner-copy-link">
                    <Copy size={14} />
                  </Button>
                  <a href={ownerInfo.setup_link} target="_blank" rel="noreferrer" title="Preview">
                    <Button size="sm" variant="outline"><ExternalLink size={14} /></Button>
                  </a>
                </div>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500">Temporary password (fallback only)</summary>
                <div className="flex gap-2 mt-2">
                  <input
                    readOnly
                    value={ownerInfo.temporary_password}
                    className="flex-1 border rounded-md px-3 py-2 text-xs font-mono bg-gray-50"
                  />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(ownerInfo.temporary_password); toast.success("Temp password copied"); }}>
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Only share this if the setup link is broken. The user will be forced to change it on first login.</p>
              </details>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setOwnerInfo(null)} className="bg-[#1a3f3a] hover:bg-[#07111F] text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
